/**
 * Vaccination Card Component
 * Displays vaccination requirements with timing and restrictions
 */

import React from 'react';
import { Syringe, Clock, Calendar, AlertCircle, Users } from 'lucide-react';
import type { VaccinationRequirement } from '@/services/api/healthRecommendations';
import { useI18nStore } from '@/stores/i18nStore';

interface VaccinationCardProps {
  vaccination: VaccinationRequirement;
  isRequired?: boolean;
}

export const VaccinationCard: React.FC<VaccinationCardProps> = ({ 
  vaccination,
  isRequired = false
}) => {
  const t = useI18nStore((state) => state.t);

  return (
    <div className={`border rounded-lg p-4 ${
      isRequired 
        ? 'bg-red-50 border-red-200' 
        : 'bg-orange-50 border-orange-200'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 p-2 rounded-lg ${
          isRequired ? 'bg-red-100' : 'bg-orange-100'
        }`}>
          <Syringe className={`h-5 w-5 ${
            isRequired ? 'text-red-600' : 'text-orange-600'
          }`} />
        </div>

        <div className="flex-1">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {vaccination.vaccineName}
              </h3>
              <p className="text-xs text-gray-500">
                {t.isoCode}: {vaccination.isoCode}
              </p>
            </div>
            {isRequired && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                {t.required}
              </span>
            )}
          </div>

          {/* Timing Information */}
          <div className="space-y-2 mt-3">
            {vaccination.minDaysBeforeDeparture && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>
                  {t.minDaysBeforeDeparture}: {vaccination.minDaysBeforeDeparture} {t.days}
                </span>
              </div>
            )}

            {vaccination.validityDays && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Clock className="h-4 w-4 text-gray-500" />
                <span>
                  {t.validity}: {vaccination.validityDays} {t.days} ({Math.floor(vaccination.validityDays / 365)} {t.years})
                </span>
              </div>
            )}

            {vaccination.boosterRequired && (
              <div className="flex items-center gap-2 text-sm text-orange-700">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">{t.boosterRequired}</span>
              </div>
            )}
          </div>

          {/* Age Restrictions */}
          {vaccination.ageRestrictions && (
            <div className="mt-3 p-2 bg-white rounded border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{t.ageRestrictions}:</span>
                {vaccination.ageRestrictions.minAge && (
                  <span>{t.from} {vaccination.ageRestrictions.minAge} {t.years}</span>
                )}
                {vaccination.ageRestrictions.maxAge && (
                  <span>{t.to} {vaccination.ageRestrictions.maxAge} {t.years}</span>
                )}
              </div>
            </div>
          )}

          {/* Contraindications */}
          {vaccination.contraindications && vaccination.contraindications.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-500 mb-1">
                {t.contraindications}:
              </div>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                {vaccination.contraindications.map((contraindication, index) => (
                  <li key={index}>{contraindication}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Source */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              {t.source}: {vaccination.source.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
